/* eslint-disable no-underscore-dangle */
import { createElement } from "react"
import {
  $getNodeByKey,
  $isTextNode,
  DecoratorNode,
  type LexicalNode,
  type NodeKey,
  type SerializedLexicalNode,
  type Spread,
} from "lexical"
import { getMergeTagComponent } from "./mergeTagComponentRegistry"

export type SerializedMergeTagNode = Spread<
  { tagId: string; tagLabel: string; type: "merge-tag"; version: 1 },
  SerializedLexicalNode
>

// eslint-disable-next-line no-undef
export class MergeTagNode extends DecoratorNode<JSX.Element> {
  __tagId: string

  __tagLabel: string

  static getType(): string {
    return "merge-tag"
  }

  static clone(node: MergeTagNode): MergeTagNode {
    return new MergeTagNode(node.__tagId, node.__tagLabel, node.__key)
  }

  constructor(tagId: string, tagLabel: string, key?: NodeKey) {
    super(key)
    this.__tagId = tagId
    this.__tagLabel = tagLabel
  }

  // eslint-disable-next-line class-methods-use-this
  createDOM(): HTMLElement {
    const span = document.createElement("span")
    span.style.display = "inline"

    return span
  }

  // eslint-disable-next-line class-methods-use-this
  updateDOM(): false {
    return false
  }

  // eslint-disable-next-line class-methods-use-this
  isInline(): boolean {
    return true
  }

  // Treat each tag as a single cursor step (arrow keys jump over the whole pill).
  // eslint-disable-next-line class-methods-use-this
  isIsolated(): boolean {
    return true
  }

  getTagId(): string {
    return this.__tagId
  }

  getTagLabel(): string {
    return this.__tagLabel
  }

  getTextContent(): string {
    return `{{${this.__tagId}}}`
  }

  // eslint-disable-next-line no-undef
  decorate(): JSX.Element {
    return createElement(getMergeTagComponent(), {
      tagId: this.__tagId,
      tagLabel: this.__tagLabel,
      nodeKey: this.__key,
    })
  }

  exportJSON(): SerializedMergeTagNode {
    return {
      tagId: this.__tagId,
      tagLabel: this.__tagLabel,
      type: "merge-tag",
      version: 1,
    }
  }

  static importJSON(
    serializedNode: SerializedMergeTagNode,
  ): MergeTagNode {
    return $createMergeTagNode(
      serializedNode.tagId,
      serializedNode.tagLabel,
    )
  }
}

export const $createMergeTagNode = (
  tagId: string,
  tagLabel: string,
): MergeTagNode => new MergeTagNode(tagId, tagLabel)

export const $isMergeTagNode = (
  node: LexicalNode | null | undefined,
): node is MergeTagNode => node instanceof MergeTagNode

export const $removeMergeTagByKey = (nodeKey: NodeKey): void => {
  const node = $getNodeByKey(nodeKey)
  if (!$isMergeTagNode(node)) return

  const previous = node.getPreviousSibling()
  const next = node.getNextSibling()
  node.remove()

  if ($isTextNode(previous)) {
    const size = previous.getTextContentSize()
    previous.select(size, size)
  } else if ($isTextNode(next)) {
    next.select(0, 0)
  } else if ($isMergeTagNode(next)) {
    next.selectPrevious(0, 0)
  } else if ($isMergeTagNode(previous)) {
    previous.selectNext(0, 0)
  }
}
